import { useTranslation } from 'react-i18next';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@monksflow/monks-ui';
import { Lock } from 'lucide-react';
import { ProblemTriangle } from '../../../components/ProblemTriangle/ProblemTriangle';
import { DatePickerButton } from './DatePickerButton';

interface GridCellProps {
  value: string;
  muted?: boolean;
  isDate?: boolean;
  mask?: string | null;
  /** Why this value breaks the field's validation pattern, if it does. */
  violation?: string | null;
  /** How loudly to paint it. A warning must not look like an error. */
  violationSeverity?: 'error' | 'warning' | null;
  selected: boolean;
  isFocus: boolean;
  editing: boolean;
  readOnly: boolean;
  /**
   * The column draws its values from a shared list, so nothing here may write.
   *
   * Separate from `readOnly` on purpose: `readOnly` also strips the mouse
   * handlers, and a bound column still has to be selectable and copyable.
   */
  isBound?: boolean;
  /**
   * `row-col`, so keyboard navigation can find the cell in the DOM and scroll
   * it into view. The coordinate has to survive to the rendered element —
   * nothing else on a `<td>` identifies which cell it is.
   */
  dataCell: string;
  lastCol: boolean;
  editDraft: string;
  /** Matches the API's cap for this sub-column, so typing stops where the save would. */
  maxLength: number;
  onMouseDown: (e: React.MouseEvent) => void;
  onMouseEnter: () => void;
  onDoubleClick: () => void;
  onEditChange: (v: string) => void;
  onEditKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onCommit: () => void;
  onDateChange: (v: string) => void;
}

/**
 * One spreadsheet cell. Cheap by default (a text `<div>`) — it only mounts an
 * `<input>` while being edited, so a full grid stays light. Selection is drawn
 * with a tinted background (in-range) + a ring on the focus cell. Date VALUE
 * cells render selectable text plus a calendar-icon `DatePickerButton` — unless
 * the column is list-bound, where there is nothing here that may be written.
 */
export function GridCell({
  value,
  muted,
  isDate,
  mask,
  violation,
  violationSeverity,
  selected,
  isFocus,
  editing,
  readOnly,
  isBound,
  dataCell,
  lastCol,
  editDraft,
  maxLength,
  onMouseDown,
  onMouseEnter,
  onDoubleClick,
  onEditChange,
  onEditKeyDown,
  onCommit,
  onDateChange,
}: GridCellProps) {
  const { t } = useTranslation('knowledge');
  // A violation tints the cell rather than blocking the edit: the value is
  // already saved, and the author needs to SEE the problem to fix it. Selection
  // still wins visually so a highlighted range doesn't turn into confetti.
  const warns = violationSeverity === 'warning';
  const cls = `relative select-none border-b border-l border-border-light p-0 ${
    lastCol ? 'border-r' : ''
  } ${
    selected ? 'bg-primary/10' : violation ? (warns ? 'bg-amber-500/10' : 'bg-destructive/10') : ''
  } ${isFocus && !editing ? 'z-10 ring-2 ring-inset ring-primary' : ''}`;

  const cellHandlers = readOnly ? {} : { onMouseDown, onMouseEnter, onDoubleClick };

  // Date cells: selectable text + a calendar-icon popover that writes directly
  // (decoupled from the edit/commit flow, which was unreliable for dates).
  if (isDate) {
    return (
      <td className={cls} data-cell={dataCell} {...(readOnly ? {} : { onMouseDown, onMouseEnter })}>
        <div className="flex h-9 items-center gap-1 px-2">
          <span
            className={`min-w-0 flex-1 truncate text-sm ${value ? 'text-foreground' : 'text-muted-foreground/40'}`}
          >
            {/* A bound column has no picker, so inviting the user to pick a date
                would point at nothing. Reads like a generated column instead. */}
            {value || (isBound ? '—' : mask || t('taxonomy.fieldsEditor.pickDate'))}
          </span>
          {!readOnly && !isBound && <DatePickerButton mask={mask} onChange={onDateChange} />}
        </div>
      </td>
    );
  }

  return (
    <td className={cls} data-cell={dataCell} {...cellHandlers}>
      {editing ? (
        <input
          // eslint-disable-next-line jsx-a11y/no-autofocus -- the input only mounts once the user starts editing, so focusing it is the interaction
          autoFocus
          value={editDraft}
          maxLength={maxLength}
          onChange={(e) => onEditChange(e.target.value)}
          onBlur={onCommit}
          onKeyDown={onEditKeyDown}
          onMouseDown={(e) => e.stopPropagation()}
          className="h-9 w-full bg-background px-2 text-sm text-foreground outline-none"
        />
      ) : (
        <div
          className={`flex h-9 items-center gap-1 truncate px-2 text-sm ${
            muted ? 'text-muted-foreground' : 'text-foreground'
          } ${!value ? 'text-muted-foreground/40' : ''}`}
        >
          <span className="min-w-0 flex-1 truncate">{value || '—'}</span>
          {violation ? (
            <ProblemTriangle message={violation} warning={warns} />
          ) : (
            /*
             * A bound cell silently refuses every edit, and without this the
             * only explanation sits on the column header, far from where the
             * user clicked. Its own trigger rather than the cell's, so the
             * cell stays a plain non-interactive element.
             *
             * Not in a read-only grid. The tooltip tells the reader to edit the
             * values in the list itself, which is advice they cannot act on here,
             * and it is one tab stop per bound cell: a 50-row shared list put
             * about a hundred of them between the grid and the controls below it,
             * all repeating one sentence.
             *
             * The per-cell trigger stays where the grid IS editable. That is
             * deliberate and tested: this table declares no `role`, so the ARIA
             * pattern puts every focusable element in the sequence - unlike the
             * record grid, which declares `role="grid"` and therefore makes its
             * own decorations `tabIndex={-1}`.
             */
            isBound &&
            !readOnly && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      aria-label={t('taxonomy.fieldsEditor.cellOwnedByList')}
                      onMouseDown={(event) => event.preventDefault()}
                      className="flex shrink-0 items-center rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <Lock className="size-3 text-muted-foreground" aria-hidden />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>{t('taxonomy.fieldsEditor.cellOwnedByList')}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )
          )}
        </div>
      )}
    </td>
  );
}
